import React, { useState, useMemo, useCallback, useEffect } from 'react';
import ReactFlow, { Controls, MiniMap } from 'reactflow';
import 'reactflow/dist/style.css';
import { palette } from '../../utils/designTokens';
import GraphNode from '../../components/GraphNode';
import {
  EMPTY_DATA_MESSAGE,
  GRAPH_MINIMAP_SHOW,
  GRAPH_MINIMAP_HIDE,
  GRAPH_MINIMAP_LABEL
} from '../../utils/strings';
import ErrorBoundary from './components/shared/ErrorBoundary';
import { fetchTraceEdges } from '../../services/traceDetailApi';
import { buildAgentGraph, EDGE_KIND_LABELS } from '../../utils/agentGraphBuilder';
import { useSessionJwt } from '../../hooks/useSessionJwt';

// ---------------------------------------------------------------------------
// Detail panel (shown when an edge/node is selected in edges mode)
// ---------------------------------------------------------------------------

const formatDuration = (durationMs) => {
  if (durationMs == null) return '—';
  const n = Number(durationMs);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString()}ms`;
};

const formatTokens = (inputTokens, outputTokens) => {
  const input = Number(inputTokens || 0);
  const output = Number(outputTokens || 0);
  if (input === 0 && output === 0) return null;
  return `${input.toLocaleString()} in / ${output.toLocaleString()} out`;
};

const getSpanName = (span) => span?.span_name || span?.name || span?.span_id || 'Span';

const resolvePrimarySpanId = (edge) => (
  edge?.target_span_id || edge?.decision_span_id || edge?.source_span_id || ''
);

const resolvePrimarySpan = (edge, spanById) => {
  const spanId = resolvePrimarySpanId(edge);
  return spanId ? spanById.get(spanId) || null : null;
};

const selectOnlyResolvableSpan = (edges, spanById, onSpanSelect) => {
  if (!Array.isArray(edges) || edges.length !== 1 || typeof onSpanSelect !== 'function') return;
  const span = resolvePrimarySpan(edges[0], spanById);
  if (span) onSpanSelect(span);
};

const getStatusLabel = (edge, span) => {
  if (edge?.has_error || span?.status_code === 'ERROR' || span?.metrics?.has_error) return 'ERROR';
  return 'OK';
};

const EdgeInstanceRow = ({ edge, index, spanById, selectedSpanId, onSpanSelect }) => {
  const span = resolvePrimarySpan(edge, spanById);
  const spanId = span?.span_id || resolvePrimarySpanId(edge);
  const isSelected = spanId && selectedSpanId === spanId;
  const status = getStatusLabel(edge, span);
  const tokens = formatTokens(edge.input_tokens, edge.output_tokens);

  const handleClick = () => {
    if (span && typeof onSpanSelect === 'function') onSpanSelect(span);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!span}
      className={`w-full px-3 py-2 text-left transition-colors ${span ? 'hover:bg-background-tertiary' : 'cursor-default opacity-70'} ${isSelected ? 'bg-accent-primary-base/10' : ''}`}
      title={span ? `Open ${getSpanName(span)}` : 'No matching span was returned for this edge'}
    >
      <div className="flex items-center gap-3 text-xs">
        <span className="w-7 shrink-0 text-text-muted">#{index + 1}</span>
        <span className={`shrink-0 font-medium ${status === 'ERROR' ? 'text-danger' : 'text-text-primary'}`}>
          {status}
        </span>
        <span className="shrink-0 text-text-muted">{formatDuration(edge.duration_ms)}</span>
        {tokens && <span className="shrink-0 text-text-muted">{tokens} tokens</span>}
        {span && (
          <span className="min-w-0 truncate text-accent-primary-base">
            {getSpanName(span)}
          </span>
        )}
      </div>
      {(edge.input_summary || edge.output_summary) && (
        <div className="mt-1 grid gap-0.5 text-xs">
          {edge.input_summary && (
            <div className="truncate">
              <span className="text-text-muted">In: </span>
              <span className="text-text-primary">{edge.input_summary}</span>
            </div>
          )}
          {edge.output_summary && (
            <div className="truncate">
              <span className="text-text-muted">Out: </span>
              <span className="text-text-primary">{edge.output_summary}</span>
            </div>
          )}
        </div>
      )}
    </button>
  );
};

const EdgeInstanceList = ({ edges, spanById, selectedSpanId, onSpanSelect }) => {
  if (!edges?.length) return null;

  return (
    <div className="divide-y divide-border-subtle">
      {edges.map((edge, i) => (
        <EdgeInstanceRow
          key={`${edge.edge_sequence ?? i}:${edge.source_span_id || ''}:${edge.target_span_id || ''}:${edge.decision_span_id || ''}`}
          edge={edge}
          index={i}
          spanById={spanById}
          selectedSpanId={selectedSpanId}
          onSpanSelect={onSpanSelect}
        />
      ))}
    </div>
  );
};

const DetailPanel = ({ item, spanById, selectedSpanId, onSpanSelect, onClose }) => {
  if (!item) return null;

  if (item.type === 'edge') {
    const { edges: edgeList, kind, sourceLabel, targetLabel, isCrossService, sourceServiceName, sourceEnvironment, targetServiceName, targetEnvironment } = item.data;
    const isTraceScope = isCrossService !== undefined;
    const crossEnv = isTraceScope && sourceEnvironment && targetEnvironment && sourceEnvironment !== targetEnvironment;
    const srcServiceLabel = isTraceScope && sourceServiceName
      ? (crossEnv ? `${sourceServiceName} · ${sourceEnvironment}` : sourceServiceName)
      : null;
    const tgtServiceLabel = isTraceScope && targetServiceName
      ? (crossEnv ? `${targetServiceName} · ${targetEnvironment}` : targetServiceName)
      : null;
    return (
      <div
        className="border-t border-border-subtle bg-background-secondary overflow-y-auto"
        style={{ maxHeight: '11rem' }}
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle">
          <div className="flex items-center gap-2 text-xs">
            <span className="font-medium text-text-primary">{sourceLabel}</span>
            {srcServiceLabel && (
              <span className="text-text-muted bg-background-tertiary px-1.5 py-0.5 rounded">{srcServiceLabel}</span>
            )}
            <span className="text-text-muted">→</span>
            <span className="font-medium text-text-primary">{targetLabel}</span>
            {tgtServiceLabel && (
              <span className="text-text-muted bg-background-tertiary px-1.5 py-0.5 rounded">{tgtServiceLabel}</span>
            )}
            {!isTraceScope && (
              <span className="text-text-muted bg-background-tertiary px-1.5 py-0.5 rounded">
                {EDGE_KIND_LABELS[kind] || kind}
              </span>
            )}
            {isTraceScope && isCrossService && (
              <span className="px-1.5 py-0.5 rounded" style={{ color: palette.accents.warm.base, background: `${palette.accents.warm.base}18` }}>
                Cross-service
              </span>
            )}
            {edgeList.length > 1 && (
              <span className="text-text-muted">{edgeList.length} calls</span>
            )}
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary text-xs px-1">✕</button>
        </div>
        <EdgeInstanceList
          edges={edgeList}
          spanById={spanById}
          selectedSpanId={selectedSpanId}
          onSpanSelect={onSpanSelect}
        />
      </div>
    );
  }

  if (item.type === 'node') {
    const { label, type, callees, relatedEdges } = item.data;
    const incoming = relatedEdges?.incoming || [];
    const outgoing = relatedEdges?.outgoing || [];
    return (
      <div
        className="border-t border-border-subtle bg-background-secondary overflow-y-auto"
        style={{ maxHeight: '14rem' }}
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle">
          <div className="flex items-center gap-2 text-xs">
            <span className="font-medium text-text-primary">{label}</span>
            <span className="text-text-muted capitalize">{type}</span>
            {(incoming.length + outgoing.length) > 0 && (
              <span className="text-text-muted">{incoming.length + outgoing.length} calls</span>
            )}
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary text-xs px-1">✕</button>
        </div>
        {callees?.length > 0 && (
          <div className="px-3 py-2 text-xs text-text-muted">Calls: {callees.join(', ')}</div>
        )}
        {outgoing.length > 0 && (
          <div>
            <div className="px-3 py-1 text-[10px] font-medium uppercase text-text-muted bg-background-tertiary">
              Outgoing
            </div>
            <EdgeInstanceList
              edges={outgoing}
              spanById={spanById}
              selectedSpanId={selectedSpanId}
              onSpanSelect={onSpanSelect}
            />
          </div>
        )}
        {incoming.length > 0 && (
          <div>
            <div className="px-3 py-1 text-[10px] font-medium uppercase text-text-muted bg-background-tertiary">
              Incoming
            </div>
            <EdgeInstanceList
              edges={incoming}
              spanById={spanById}
              selectedSpanId={selectedSpanId}
              onSpanSelect={onSpanSelect}
            />
          </div>
        )}
      </div>
    );
  }

  return null;
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const AgentGraphTab = ({ traceId, appId, env, spans = [], selectedSpan, onSpanSelect }) => {
  const { sessionId, getJwt } = useSessionJwt();
  const [apiEdges, setApiEdges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showMiniMap, setShowMiniMap] = useState(true);
  const [isInteractive, setIsInteractive] = useState(true);

  useEffect(() => {
    const jwt = getJwt();
    if (!traceId || !jwt) return;
    let cancelled = false;
    setLoading(true);
    setFetchError(null);
    setSelectedItem(null);
    fetchTraceEdges(traceId, appId, env, jwt, 'semantic')
      .then(data => { if (!cancelled) { setApiEdges(data); setLoading(false); } })
      .catch(err => { if (!cancelled) { setFetchError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [traceId, appId, env, sessionId, getJwt]);

  const nodeTypes = useMemo(() => ({ graphNode: GraphNode }), []);

  const spanById = useMemo(() => {
    const map = new Map();
    (spans || []).forEach(span => {
      if (span?.span_id) map.set(span.span_id, span);
    });
    return map;
  }, [spans]);

  // Build agent/entity graph from API edges using the shared builder.
  // Never uses span IDs as node IDs — blank target_span_id rows are handled by
  // the shared builder which derives target node IDs from to_type/to_name.
  const { layoutedNodes, baseEdges } = useMemo(() => {
    if (apiEdges.length > 0) return buildAgentGraph(apiEdges);
    return { layoutedNodes: [], baseEdges: [] };
  }, [apiEdges]);

  const edgeInstancesByNodeId = useMemo(() => {
    const related = new Map();
    const ensure = (id) => {
      if (!related.has(id)) related.set(id, { incoming: [], outgoing: [] });
      return related.get(id);
    };

    baseEdges.forEach(edge => {
      const instances = edge.data?.edges || [];
      if (edge.source) ensure(edge.source).outgoing.push(...instances);
      if (edge.target) ensure(edge.target).incoming.push(...instances);
    });

    return related;
  }, [baseEdges]);

  const selectedSpanId = selectedSpan?.span_id || null;
  const selectedAggregateId = useMemo(() => {
    if (!selectedSpanId) return null;
    const edge = baseEdges.find(e => (e.data?.edges || []).some(instance => (
      instance.source_span_id === selectedSpanId ||
      instance.target_span_id === selectedSpanId ||
      instance.decision_span_id === selectedSpanId
    )));
    return edge?.id || null;
  }, [baseEdges, selectedSpanId]);

  // Apply selection state separately so the graph doesn't rebuild on every click
  const { rfNodes, rfEdges } = useMemo(() => {
    const selEdgeId  = selectedItem?.type === 'edge' ? selectedItem.id : null;
    const selNodeId  = selectedItem?.type === 'node' ? selectedItem.id : null;
    return {
      rfNodes: layoutedNodes.map(n => ({ ...n, selected: n.id === selNodeId })),
      rfEdges: baseEdges.map(e => {
        const isSelected = e.id === selEdgeId;
        const hasSelectedSpan = e.id === selectedAggregateId;
        if (!isSelected && !hasSelectedSpan) return e;
        return {
          ...e,
          selected: isSelected,
          animated: hasSelectedSpan,
          style: {
            ...e.style,
            strokeWidth: e.style.strokeWidth + (isSelected ? 1 : 0.5),
          }
        };
      }),
    };
  }, [layoutedNodes, baseEdges, selectedItem, selectedAggregateId]);

  const onNodeClick = useCallback((event, node) => {
    event.stopPropagation();
    const relatedEdges = edgeInstancesByNodeId.get(node.id) || { incoming: [], outgoing: [] };
    setSelectedItem({ type: 'node', id: node.id, data: { ...node.data, relatedEdges } });
    selectOnlyResolvableSpan([...relatedEdges.outgoing, ...relatedEdges.incoming], spanById, onSpanSelect);
  }, [edgeInstancesByNodeId, spanById, onSpanSelect]);

  const onEdgeClick = useCallback((event, edge) => {
    event.stopPropagation();
    setSelectedItem({ type: 'edge', id: edge.id, data: edge.data });
    selectOnlyResolvableSpan(edge.data?.edges || [], spanById, onSpanSelect);
  }, [spanById, onSpanSelect]);

  const onPaneClick = useCallback(() => setSelectedItem(null), []);


  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-text-muted text-sm">Loading agent flow...</div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-status-danger text-sm">Failed to load edges: {fetchError}</div>
      </div>
    );
  }

  if (!rfNodes.length) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-text-muted">{EMPTY_DATA_MESSAGE}</div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="flex flex-col h-full">
        <div className="flex-1 min-h-0">
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            nodeTypes={nodeTypes}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={true}
            fitView
            proOptions={{ hideAttribution: true }}
            panOnDrag={isInteractive}
            zoomOnScroll={isInteractive}
            zoomOnPinch={isInteractive}
            zoomOnDoubleClick={isInteractive}
            style={{ background: palette.neutrals.canvas }}
            onlyRenderVisibleElements={true}
            elevateNodesOnSelect={false}
            nodeOrigin={[0.5, 0.5]}
          >
            <Controls
              showInteractive={true}
              onInteractiveChange={(interactive) => setIsInteractive(interactive)}
            />
            {showMiniMap && (
              <MiniMap
                nodeColor={palette.text.primary}
                nodeStrokeColor={palette.text.primary}
                maskColor="rgba(0, 0, 0, 0.7)"
                style={{
                  background: palette.neutrals.canvas,
                  border: `2px solid ${palette.neutrals.borderStrong}`,
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)'
                }}
                position="bottom-right"
                nodeBorderRadius={4}
                nodeStrokeWidth={10}
                zoomable
                pannable
              />
            )}
            <div className="absolute z-10" style={{ bottom: '20px', right: '20px' }}>
              <button
                onClick={() => setShowMiniMap(!showMiniMap)}
                className="px-3 py-1.5 text-xs bg-background-secondary border border-border-subtle rounded hover:bg-surface-hover text-text-secondary hover:text-text-primary transition-colors shadow-md"
                title={showMiniMap ? 'Hide navigation minimap' : 'Show navigation minimap'}
              >
                {showMiniMap ? GRAPH_MINIMAP_HIDE : GRAPH_MINIMAP_SHOW} {GRAPH_MINIMAP_LABEL}
              </button>
            </div>
          </ReactFlow>
        </div>
        {selectedItem && (
          <DetailPanel
            item={selectedItem}
            spanById={spanById}
            selectedSpanId={selectedSpanId}
            onSpanSelect={onSpanSelect}
            onClose={() => setSelectedItem(null)}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default AgentGraphTab;
